import {
  Controller,
  FieldErrors,
  Control,
  FieldErrorsImpl,
  Merge,
  FieldError,
} from "react-hook-form";
import TextField from "@mui/material/TextField";
import { FocusEvent } from "react";
import { Theme } from "@emotion/react";
import { SxProps } from "@mui/material";
import { forwardRef } from "react";
import { InputMask, type InputMaskProps } from "@react-input/mask";
import { getTextAfterLastDot } from "../utils/utils";

const ForwardedInputMask = (mask: string) =>
  forwardRef<HTMLInputElement, InputMaskProps>((props, forwardedRef) => {
    return (
      <InputMask ref={forwardedRef} mask={mask} replacement="_" {...props} />
    );
  });

export default function TextInput(props: {
  fieldName: string;
  label: string;
  control: Control<any, any>;
  errors:
    | FieldErrors<any>
    | FieldErrorsImpl<any>
    | Merge<FieldError, FieldErrorsImpl<any>>
    | undefined;
  type?: string;
  fontSize?: number;
  width?: number | string;
  size?: "small" | "medium";
  sx?: SxProps<Theme>;
  style?: React.CSSProperties;
  required?: boolean;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  mask?: string;
  shrink?: boolean;
  multiline?: boolean;
  isFromArray?: boolean;
}) {
  const {
    fieldName,
    control,
    fontSize,
    errors,
    label,
    disabled,
    mask,
    shrink,
    multiline,
    isFromArray,
  } = props;

  const errorKey = isFromArray ? getTextAfterLastDot(fieldName) : fieldName;

  const errorMessage = (errors &&
    (typeof errors[errorKey]?.message === "string"
      ? errors[errorKey]?.message
      : undefined)) as string | undefined;

  return (
    <Controller
      name={fieldName}
      control={control}
      render={({ field }) => (
        <TextField
          disabled={disabled}
          multiline={multiline}
          rows={multiline ? 4 : 1}
          style={props.style}
          size="small"
          sx={{
            ...props.sx,
            "& .MuiOutlinedInput-root": {
              borderRadius: "4px",
            },
          }}
          InputProps={{
            style: {
              fontSize: fontSize ?? 16,
            },
            inputComponent: mask ? ForwardedInputMask(mask) : undefined,
          }}
          InputLabelProps={{
            style: { fontSize: fontSize ?? 16 },
            shrink: shrink,
          }}
          value={field.value ?? ""}
          onChange={(e) => {
            field.onChange(e);
          }}
          type={props.type ?? "text"}
          label={label}
          error={
            !isFromArray
              ? errors && !!errors[fieldName]
              : errorMessage
              ? true
              : false
          }
          helperText={errorMessage}
          required={props.required}
          onBlur={props.onBlur}
        />
      )}
    />
  );
}
